import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const palette = {
  forest: '#1f3d2e',
  forestDark: '#152a20',
  forestLight: '#2c5642',
  cream: '#f5f1e8',
  creamDeep: '#ebe5d6',
  ink: '#1a1a1a',
  inkMuted: '#5b5b5b',
  rose: '#d8c4b6',
  paper: '#ffffff',
};

let theme = createTheme({
  cssVariables: true,
  shape: { borderRadius: 4 },
  palette: {
    mode: 'light',
    primary: {
      main: palette.forest,
      dark: palette.forestDark,
      light: palette.forestLight,
      contrastText: palette.cream,
    },
    secondary: {
      main: palette.rose,
      contrastText: palette.ink,
    },
    background: {
      default: palette.cream,
      paper: palette.paper,
    },
    text: {
      primary: palette.ink,
      secondary: palette.inkMuted,
    },
    divider: 'rgba(31, 61, 46, 0.16)',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
      fontWeight: 600,
      lineHeight: 1.05,
      letterSpacing: '-0.015em',
    },
    h2: {
      fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
      fontWeight: 600,
      lineHeight: 1.1,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
      fontWeight: 600,
      lineHeight: 1.15,
    },
    h4: {
      fontFamily: '"Playfair Display", Georgia, "Times New Roman", serif',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    overline: {
      fontFamily: '"Inter", sans-serif',
      letterSpacing: '0.18em',
      fontWeight: 600,
      fontSize: '0.78rem',
    },
    button: {
      fontFamily: '"Inter", sans-serif',
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
    body1: { lineHeight: 1.65 },
    body2: { lineHeight: 1.6 },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 2,
          paddingInline: 28,
          paddingBlock: 12,
          fontSize: '0.82rem',
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', fullWidth: true },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: palette.paper,
          '& fieldset': { borderColor: 'rgba(31, 61, 46, 0.22)' },
          '&:hover fieldset': { borderColor: 'rgba(31, 61, 46, 0.45)' },
          [`&.Mui-focused fieldset`]: { borderColor: palette.forest },
          // Neutralize iOS Safari / Chrome autofill background so it matches the cream theme
          '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
            {
              WebkitBoxShadow: `0 0 0 1000px ${palette.paper} inset`,
              WebkitTextFillColor: palette.ink,
              caretColor: palette.ink,
              transition: 'background-color 5000s ease-in-out 0s',
            },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: palette.inkMuted,
          fontWeight: 500,
          '&.Mui-focused': { color: palette.forest },
        },
        // Floating label — when shrunk to the notch, give it a small bg so it
        // never overlaps autofill yellow/blue or the input border
        outlined: {
          backgroundColor: palette.paper,
          paddingLeft: 4,
          paddingRight: 4,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.cream,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
  },
});

theme = responsiveFontSizes(theme, { factor: 2.4 });

export { theme, palette };
